import { useNavigate } from '@tanstack/react-router';
import { TranslucentMessageType } from 'components/TranslucentMessage/TranslucentMessage.component';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSelfLimitation,
  selectSelfLimitationPending,
} from 'services/playResponsibly/playResponsibly.selectors';
import useQueryParams from 'utils/hooks/useQueryParams';

export function useCurrentRechargeableAmount() {
  const selfLimitation = useSelector(selectSelfLimitation);
  const currentSelfLimitationWrapper = selfLimitation?.find(
    (element) => !!element?.currentSelfLimitation,
  );
  const currentSelfLimitation =
    currentSelfLimitationWrapper?.currentSelfLimitation;

  const threshold = currentSelfLimitation?.amount ?? 0;
  const consumedAmount = currentSelfLimitationWrapper?.consumedAmount ?? 0;

  const currentAmount = threshold - consumedAmount;

  return currentAmount > 0 ? currentAmount : 0;
}

export function useStandaloneEditRechargeLimitsFlow() {
  const popupDuration = 3000;
  const modalQueryParamKey = 'modifica-limiti-ricarica';
  const navigate = useNavigate();
  const { searchParams } = useQueryParams();

  const selfLimitation = useSelector(selectSelfLimitation);
  const nextSelfLimitation = selfLimitation?.find(
    (element) => !!element?.nextSelfLimitation,
  )?.nextSelfLimitation;

  const selfLimitationPending = useSelector(selectSelfLimitationPending);

  const [responseMessage, setResponseMessage] = useState<{
    show: boolean;
    type: TranslucentMessageType;
  }>({ show: false, type: 'confirm' });

  const showTemporizedConfirmMsg = (type: TranslucentMessageType) => {
    setResponseMessage({ show: true, type });
    setTimeout(closeMsg, popupDuration);
  };

  const closeMsg = () => {
    setResponseMessage({ ...responseMessage, show: false });
  };

  return {
    showEditRechargeLimitModal: searchParams[modalQueryParamKey] === 'true',
    responseMessage,
    showLimitWaitingUpdateAlert: !isEmpty(nextSelfLimitation),
    showLimitRequestedUpdateAlert: !isEmpty(selfLimitationPending),
    heading: 'text.preferences.limitWeekly.rechargeLimit',
    openModifyLimitsModal: () => {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, [modalQueryParamKey]: true }),
      });
    },
    closeEditLimitsModal: () => {
      navigate({
        to: '.',
        search: (prev) => {
          delete prev[modalQueryParamKey];
          return prev;
        },
      });
    },
    handleSelfLimitationModifiedImmediately: () =>
      showTemporizedConfirmMsg('confirm'),
    handleSelfLimitationModifiedNext: () => showTemporizedConfirmMsg('confirm'),
    handleSuccessfullyRequestedOverApprovalAmount: () =>
      showTemporizedConfirmMsg('confirm'),
    handleError: () => showTemporizedConfirmMsg('error'),
    closeMsg,
  };
}

export function formatUnixToTimeAndDate(unixMs: number) {
  const dateObj = new Date(unixMs);

  const date = dateObj.toLocaleDateString('en-GB');
  const time = dateObj.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return { date, time };
}
